<script>
    import { currentPage, recommendationsStore, userStore, currentRoutineStore } from '../store.js';
    import { navigate } from '../router.js';
    import { onMount } from 'svelte';
    import { get } from 'svelte/store';
    import Banner from '../components/Banner.svelte';
    import Button from '../components/Button.svelte';
    import Card from '../components/Card.svelte';
    import Chip from '../components/Chip.svelte';
    import Field from '../components/Field.svelte';
    import Stat from '../components/Stat.svelte';

    let searchQuery = '';
    let searchResults = [];
    let isSearching = false;
    let selectedProduct = null;
    let analysis = null;
    let isAnalyzing = false;
    let scanMode = 'search';
    let screenshotPreview = '';
    let screenshotMatches = [];
    let detectedText = '';
    let detectionError = '';
    let isDetecting = false;
    let userConcerns = [];
    let skinType = '';
    let profileSource = 'none';
    let activeRoutine = [];
    let searchTimeout;

    // The decision the screen exists to deliver maps onto the primitives'
    // three verdict tones. Everything else on the page is reasoning and stays
    // neutral, so one colour per result is all the user ever sees.
    const VERDICT_TONE = { buy: 'success', swap: 'caution', wait: 'caution', caution: 'caution', skip: 'danger' };
    const VERDICT_INK = { success: 'var(--success)', caution: 'var(--caution)', danger: 'var(--danger)', default: 'var(--text-muted)' };
    const ALERT_TONE = { risk: 'danger', caution: 'caution', swap: 'caution', timing: 'info', support: 'info', good: 'success' };
    const COMPARE_TONE = { swap: 'caution', duplicate: 'caution', overlap: 'info', addition: 'info', 'no-routine': 'info' };

    $: hasSkinProfile = userConcerns.length > 0;
    $: isPersonalizedAnalysis = analysis && analysis.hasPersonalizedConcerns;
    $: isLoggedIn = $userStore.isAuthenticated;
    $: routineComparison = analysis ? compareToRoutine(analysis.product) : null;
    $: routineAlerts = analysis ? buildRoutineAlerts(analysis) : [];
    $: nextMove = analysis ? buildNextMove(analysis) : null;

    $: verdictTone = isPersonalizedAnalysis && nextMove ? (VERDICT_TONE[nextMove.tone] || 'default') : 'default';
    $: verdictInk = VERDICT_INK[verdictTone] || VERDICT_INK.default;
    $: verdictEyebrow = isPersonalizedAnalysis ? 'SkinGenie verdict' : 'General product lookup';
    $: verdictHeadline = isPersonalizedAnalysis && nextMove
        ? nextMove.decision
        : (isPersonalizedAnalysis ? getScoreLabel(analysis.compatibilityScore) : 'No personal match yet');
    $: verdictBody = isPersonalizedAnalysis && nextMove
        ? nextMove.summary
        : ((analysis && analysis.verdict) || 'SkinGenie found this product, but it needs your skin profile before giving a personal match score.');
    // bullets[0] always restates the score, which the Stat beside it already says.
    $: verdictChips = isPersonalizedAnalysis && nextMove
        ? nextMove.bullets.filter(bullet => bullet !== `${Number(analysis.compatibilityScore || 0)}% skin match`)
        : [];

    onMount(async () => {
        // Try to load concerns from the user's profile first (most reliable)
        try {
            const authRes = await fetch('/api/is_authenticated', { credentials: 'include' });
            if (authRes.ok) {
                const authData = await authRes.json();
                const profileRes = await fetch(`/api/profile/${authData.userId}`, { credentials: 'include' });
                if (profileRes.ok) {
                    const profileData = await profileRes.json();
                    // Get concerns from the most recent face analysis
                    if (profileData.faceAnalyses && profileData.faceAnalyses.length > 0) {
                        const latest = profileData.faceAnalyses[0];
                        try {
                            let concerns = latest.concerns;
                            if (typeof concerns === 'string') concerns = JSON.parse(concerns);
                            if (typeof concerns === 'string') concerns = JSON.parse(concerns);
                            if (Array.isArray(concerns)) {
                                userConcerns = concerns.map(c => typeof c === 'string' ? c : (c.concern || c));
                                if (userConcerns.length > 0) profileSource = 'saved';
                            }
                        } catch (e) {}
                    }
                    // Get skin type from user info
                    if (profileData.userInfo && profileData.userInfo.skinType) {
                        skinType = profileData.userInfo.skinType;
                    }
                }
            }
        } catch (e) {
            console.error('Error loading profile for scanner:', e);
        }

        // Fallback to the temporary skin profile from this browser session.
        if (userConcerns.length === 0) {
            const savedSkinProfile = localStorage.getItem('skinProfile');
            if (savedSkinProfile) {
                try {
                    const profile = JSON.parse(savedSkinProfile);
                    userConcerns = profile.areasOfImprovement || profile.AOIAreas || [];
                    skinType = skinType || profile.skinType || '';
                    if (userConcerns.length > 0) profileSource = 'local';
                } catch (e) {}
            }
        }

        // Older fallback from the pre-profile recommendation flow.
        if (userConcerns.length === 0) {
            const savedFormData = localStorage.getItem('formDataForRecommendations');
            if (savedFormData) {
                try {
                    const formData = JSON.parse(savedFormData);
                    userConcerns = formData.areasOfImprovement || [];
                    skinType = skinType || formData.skinType || '';
                    if (userConcerns.length > 0) profileSource = 'local';
                } catch (e) {}
            }
        }

        activeRoutine = await loadActiveRoutine();
    });

    function debounceSearch() {
        clearTimeout(searchTimeout);
        if (searchQuery.length < 2) {
            searchResults = [];
            return;
        }
        searchTimeout = setTimeout(doSearch, 300);
    }

    async function doSearch() {
        isSearching = true;
        try {
            const response = await fetch(`/api/product-search?q=${encodeURIComponent(searchQuery)}`);
            if (response.ok) {
                searchResults = await response.json();
            }
        } catch (e) {
            console.error('Search error:', e);
        }
        isSearching = false;
    }

    async function analyzeProduct(product) {
        selectedProduct = product;
        searchResults = [];
        screenshotMatches = [];
        searchQuery = product.name;
        isAnalyzing = true;
        analysis = null;

        try {
            const response = await fetch('/api/product-analyze', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    productId: product.id,
                    concerns: userConcerns,
                    skinType: skinType
                })
            });

            if (response.ok) {
                analysis = await response.json();
            }
        } catch (e) {
            console.error('Analysis error:', e);
        }
        isAnalyzing = false;
    }

    async function handleScreenshotUpload(event) {
        const file = event.target.files && event.target.files[0];
        if (!file) return;

        screenshotPreview = URL.createObjectURL(file);
        screenshotMatches = [];
        detectedText = '';
        detectionError = '';
        analysis = null;
        selectedProduct = null;
        isDetecting = true;

        const formData = new FormData();
        formData.append('screenshot', file);

        try {
            const response = await fetch('/api/product-detect-from-image', {
                method: 'POST',
                body: formData
            });
            const data = await response.json();

            if (response.ok) {
                detectedText = data.detectedText || '';
                screenshotMatches = data.matches || [];
                if (screenshotMatches.length === 0) {
                    detectionError = 'I could read the screenshot, but I could not match it to the catalog yet. Try searching the product name instead.';
                }
            } else {
                detectionError = data.error || 'Unable to detect a product from this screenshot.';
            }
        } catch (e) {
            console.error('Screenshot detection error:', e);
            detectionError = 'Unable to process this screenshot right now. Try searching by product name.';
        }

        isDetecting = false;
    }

    function clearSelection() {
        selectedProduct = null;
        analysis = null;
        searchQuery = '';
        searchResults = [];
        screenshotMatches = [];
        detectedText = '';
        detectionError = '';
    }

    function goToSkinAnalysis() {
        navigate('face-analysis');
    }

    function signInToSave() {
        const oldUserId = localStorage.getItem('UserId');
        const suffix = oldUserId ? `&oldUserId=${encodeURIComponent(oldUserId)}` : '';
        window.location.href = `/api/login?currentPage=scanner${suffix}`;
    }

    async function loadActiveRoutine() {
        if (isLoggedIn) {
            try {
                const response = await fetch('/api/current-routine', { credentials: 'include' });
                if (response.ok) {
                    const data = await response.json();
                    if (data.items && data.items.length > 0) {
                        localStorage.setItem('currentRoutine', JSON.stringify(data.items));
                        currentRoutineStore.set(data.items);
                        return data.items;
                    }
                }
            } catch (e) {
                console.error('Unable to load current routine:', e);
            }
        }

        const currentRoutine = get(currentRoutineStore);
        if (currentRoutine && currentRoutine.length > 0) return currentRoutine;

        const savedCurrentRoutine = localStorage.getItem('currentRoutine');
        if (savedCurrentRoutine) {
            try {
                const parsed = JSON.parse(savedCurrentRoutine) || [];
                if (parsed.length > 0) {
                    currentRoutineStore.set(parsed);
                    return parsed;
                }
            } catch (e) {}
        }

        const fromStore = get(recommendationsStore);
        if (fromStore && fromStore.length > 0) return fromStore;

        const saved = localStorage.getItem('activeRoutineRecommendations');
        if (!saved) return [];

        try {
            return JSON.parse(saved) || [];
        } catch (e) {
            return [];
        }
    }

    function normalizeType(value) {
        return String(value || '').toLowerCase().replace(/\s+/g, '_');
    }

    function normalizeText(value) {
        return String(value || '').toLowerCase().replace(/[_-]/g, ' ');
    }

    function routineIngredients() {
        return activeRoutine.flatMap(item => {
            const explained = item.AOIExplained || {};
            return Object.values(explained).flat().map(value => normalizeText(value));
        });
    }

    function productSignals(productAnalysis) {
        const product = productAnalysis?.product || {};
        const text = [
            product.name,
            product.brand,
            product.productType,
            ...(productAnalysis?.benefits || []).map(benefit => benefit.concern),
            ...(productAnalysis?.productRoleBenefits || []),
            ...(productAnalysis?.comedogenicIngredients || []).map(ing => ing.ingredient)
        ].map(normalizeText).join(' ');

        return {
            hasRetinoid: /\b(retinol|retinal|retinoid|retinyl|adapalene|tretinoin)\b/.test(text),
            hasExfoliant: /\b(aha|bha|pha|glycolic|lactic|mandelic|salicylic|azelaic|exfoliant|peel)\b/.test(text),
            hasVitaminC: /\b(vitamin c|ascorbic|l-ascorbic|tetrahexyldecyl ascorbate)\b/.test(text),
            isSunscreen: normalizeType(product.productType).includes('sunscreen') || /\b(spf|sunscreen|broad spectrum)\b/.test(text),
            isCleanser: normalizeType(product.productType).includes('cleanser'),
            isExfoliantStep: normalizeType(product.productType).includes('exfoliant') || normalizeType(product.productType).includes('toner'),
            hasNiacinamide: /\bniacinamide\b/.test(text),
            hasFragrance: /\b(fragrance|parfum|limonene|linalool|citral|geraniol)\b/.test(text)
        };
    }

    function routineSignals() {
        const ingredients = routineIngredients().join(' ');
        const routineText = activeRoutine.map(item => [
            item.ProductName || item.productName,
            item.ProductType || item.productType,
            item.notes,
            ...(item.AreasOfImprovementAddressed || [])
        ].map(normalizeText).join(' ')).join(' ');
        const text = `${ingredients} ${routineText}`;

        return {
            hasRetinoid: /\b(retinol|retinal|retinoid|retinyl|adapalene|tretinoin)\b/.test(text),
            hasExfoliant: /\b(aha|bha|pha|glycolic|lactic|mandelic|salicylic|azelaic|exfoliant|peel)\b/.test(text),
            hasVitaminC: /\b(vitamin c|ascorbic|l-ascorbic|tetrahexyldecyl ascorbate)\b/.test(text),
            hasSunscreen: activeRoutine.some(item => normalizeType(item.ProductType || item.productType).includes('sunscreen') || normalizeText(item.ProductName || item.productName).includes('spf')),
            cleanserCount: activeRoutine.filter(item => normalizeType(item.ProductType || item.productType).includes('cleanser')).length,
            exfoliantCount: activeRoutine.filter(item => {
                const type = normalizeType(item.ProductType || item.productType);
                const name = normalizeText(item.ProductName || item.productName);
                return type.includes('exfoliant') || name.includes('peel') || name.includes('aha') || name.includes('bha') || name.includes('salicylic');
            }).length
        };
    }

    function routineItemName(item) {
        return item.ProductName || item.productName || 'routine product';
    }

    function routineItemType(item) {
        return item.ProductType || item.productType || '';
    }

    function routineItemText(item) {
        return [
            routineItemName(item),
            routineItemType(item),
            item.notes,
            ...(item.AreasOfImprovementAddressed || [])
        ].map(normalizeText).join(' ');
    }

    function concernOverlapForProduct(productAnalysis) {
        const benefits = (productAnalysis?.benefits || []).map(benefit => normalizeText(benefit.concern));
        const routineConcerns = activeRoutine
            .flatMap(item => item.AreasOfImprovementAddressed || [])
            .map(normalizeText);

        if (routineConcerns.length === 0) return [];
        return benefits.filter(benefit => routineConcerns.includes(benefit));
    }

    function buildRoutineAlerts(productAnalysis) {
        if (!isPersonalizedAnalysis || !productAnalysis) return [];

        const alerts = [];
        const product = productAnalysis.product || {};
        const productSignal = productSignals(productAnalysis);
        const routineSignal = routineSignals();
        const productType = normalizeType(product.productType);
        const sameStep = activeRoutine.find(item => normalizeType(item.ProductType || item.productType) === productType);

        if (sameStep) {
            alerts.push({
                tone: 'swap',
                title: 'Swap, Do Not Stack',
                body: `You already have ${sameStep.ProductName || sameStep.productName} in this step. Treat this as a possible replacement, not an extra layer.`,
            });
        }

        if (productSignal.hasRetinoid && routineSignal.hasExfoliant) {
            alerts.push({
                tone: 'caution',
                title: 'Retinoid + Exfoliant Caution',
                body: 'Your routine already appears to include exfoliating acids. Introduce retinoids slowly and avoid using them on the same night as acids.',
            });
        }

        if (productSignal.hasExfoliant && routineSignal.hasRetinoid) {
            alerts.push({
                tone: 'caution',
                title: 'Acid + Retinoid Caution',
                body: 'Your routine appears to include a retinoid. Keep exfoliating acids on separate nights to reduce irritation risk.',
            });
        }

        if (productSignal.hasExfoliant && routineSignal.exfoliantCount > 0) {
            alerts.push({
                tone: 'caution',
                title: 'Too Much Exfoliation Risk',
                body: 'This looks like another exfoliating product. More exfoliation is not always better, especially with redness, dryness, or barrier sensitivity.',
            });
        }

        if (productSignal.isCleanser && routineSignal.cleanserCount > 0) {
            alerts.push({
                tone: 'swap',
                title: 'Duplicate Cleanser Step',
                body: 'This is best evaluated as a cleanser swap. Adding a second cleanser may not improve results unless you need makeup or sunscreen removal.',
            });
        }

        if (productSignal.hasVitaminC && routineSignal.hasRetinoid) {
            alerts.push({
                tone: 'timing',
                title: 'Timing Note',
                body: 'Vitamin C often fits best in the morning, while retinoids usually belong at night. Split them if your skin feels sensitive.',
            });
        }

        if (productAnalysis.breakoutRisk !== 'low') {
            alerts.push({
                tone: 'risk',
                title: `${sentenceCase(productAnalysis.breakoutRisk)} Breakout Risk`,
                body: 'Patch test this before committing, especially if acne or clogged pores are part of your skin profile.',
            });
        }

        if (!routineSignal.hasSunscreen && !productSignal.isSunscreen && (productSignal.hasRetinoid || productSignal.hasExfoliant || productSignal.hasVitaminC)) {
            alerts.push({
                tone: 'support',
                title: 'Add Sunscreen Support',
                body: 'Actives work best with daily sunscreen. SkinGenie should add or prioritize SPF before increasing active treatments.',
            });
        }

        if (productSignal.hasFragrance && userConcerns.some(concern => normalizeText(concern).includes('redness'))) {
            alerts.push({
                tone: 'risk',
                title: 'Redness Sensitivity Note',
                body: 'This may contain fragrance-related ingredients. If redness is a priority, patch test before using it widely.',
            });
        }

        if (alerts.length === 0) {
            alerts.push({
                tone: 'good',
                title: 'No Major Routine Conflicts',
                body: 'SkinGenie did not find an obvious duplicate step, active conflict, or major routine gap from the current data.',
            });
        }

        return alerts.slice(0, 4);
    }

    function buildNextMove(productAnalysis) {
        if (!isPersonalizedAnalysis || !productAnalysis) return null;

        const score = Number(productAnalysis.compatibilityScore || 0);
        const breakoutRisk = productAnalysis.breakoutRisk || 'low';
        const benefits = productAnalysis.benefits || [];
        const notAddressed = productAnalysis.notAddressed || [];
        const product = productAnalysis.product || {};
        const productType = String(product.productType || 'product').replace(/_/g, ' ');
        const cautionAlerts = routineAlerts.filter(alert => ['risk', 'caution'].includes(alert.tone));
        const swapAlerts = routineAlerts.filter(alert => alert.tone === 'swap');
        const supportAlerts = routineAlerts.filter(alert => ['support', 'timing'].includes(alert.tone));
        const primaryConcern = benefits[0]?.concern;
        const hasRoutine = activeRoutine.length > 0;
        const hasMeaningfulBenefit = benefits.length > 0 && benefits.length >= Math.min(2, notAddressed.length);
        const riskCopy = breakoutRisk === 'low'
            ? 'Low breakout risk from known pore-clogging ingredients.'
            : `${sentenceCase(breakoutRisk)} breakout risk, so patch testing matters.`;

        if (!hasRoutine) {
            return {
                tone: 'wait',
                decision: 'Build your routine first',
                summary: 'This can be checked against your skin profile, but SkinGenie needs your current routine to know whether it belongs, overlaps, or creates active conflicts.',
                bullets: ['No saved routine yet', `${score}% skin match`, riskCopy],
                primaryAction: 'Build My Routine',
                primaryTarget: 'routine'
            };
        }

        if (swapAlerts.length > 0 && score >= 70 && cautionAlerts.length === 0) {
            return {
                tone: 'swap',
                decision: 'Consider it as a swap',
                summary: `This looks most useful as a replacement for your current ${productType} step, not as another layer.`,
                bullets: [`${score}% skin match`, primaryConcern ? `Targets ${primaryConcern}` : 'Targets at least one concern', 'Avoid stacking duplicate steps'],
                primaryAction: 'Compare Routine',
                primaryTarget: 'routine'
            };
        }

        if (swapAlerts.length > 0) {
            return {
                tone: 'skip',
                decision: 'Do not add it yet',
                summary: 'It overlaps with a step you already have, and the current risk or match signals are not strong enough to justify adding it on top.',
                bullets: [`${score}% skin match`, swapAlerts[0].title, cautionAlerts[0]?.title || riskCopy],
                primaryAction: 'Review Routine',
                primaryTarget: 'routine'
            };
        }

        if (score >= 82 && cautionAlerts.length === 0) {
            return {
                tone: 'buy',
                decision: 'Strong buy candidate',
                summary: 'This is a good fit for your current profile and does not show a major routine conflict from the data SkinGenie has.',
                bullets: [`${score}% skin match`, primaryConcern ? `Targets ${primaryConcern}` : 'Matches your profile', riskCopy],
                primaryAction: 'View Product',
                primaryTarget: 'store'
            };
        }

        if (score >= 60 && cautionAlerts.length > 0) {
            return {
                tone: 'caution',
                decision: 'Patch test before buying in',
                summary: 'The product has useful signals, but your routine or breakout profile has enough caution that it should be introduced slowly.',
                bullets: [`${score}% skin match`, cautionAlerts[0].title, supportAlerts[0]?.title || 'Introduce one change at a time'],
                primaryAction: 'Review Safety Notes',
                primaryTarget: 'safety'
            };
        }

        if (score < 45) {
            return {
                tone: 'skip',
                decision: 'Skip for now',
                summary: 'This is unlikely to be the best next purchase for your current skin goals. Look for a product that targets more of your top concerns.',
                bullets: [`${score}% skin match`, benefits.length ? `Only helps ${benefits.length} concern${benefits.length === 1 ? '' : 's'}` : 'No direct concern match', `${notAddressed.length} concerns not addressed`],
                primaryAction: 'Find Better Match',
                primaryTarget: 'search'
            };
        }

        if (!hasMeaningfulBenefit) {
            return {
                tone: 'wait',
                decision: 'Not your highest-impact buy',
                summary: 'It may be a fine product, but it does not appear to move enough of your current skin priorities to be a top recommendation.',
                bullets: [`${score}% skin match`, benefits.length ? `Helps ${benefits.length} concern${benefits.length === 1 ? '' : 's'}` : 'No clear priority match', `${notAddressed.length} concerns not addressed`],
                primaryAction: 'Search Another Product',
                primaryTarget: 'search'
            };
        }

        return {
            tone: 'buy',
            decision: 'Good add-on',
            summary: 'This can make sense as an additional product if you introduce it gradually and keep your routine simple.',
            bullets: [`${score}% skin match`, primaryConcern ? `Targets ${primaryConcern}` : 'Targets your profile', riskCopy],
            primaryAction: 'View Product',
            primaryTarget: 'store'
        };
    }

    function compareToRoutine(product) {
        if (!product || activeRoutine.length === 0) {
            return {
                status: 'no-routine',
                title: 'Compare With Routine',
                message: 'Import your current routine first, then SkinGenie can tell you whether this belongs, replaces a step, or creates overlap.',
                action: 'Import My Routine'
            };
        }

        const productType = normalizeType(product.productType);
        const sameStep = activeRoutine.find(item => normalizeType(routineItemType(item)) === productType);
        const productText = normalizeText(`${product.name} ${product.brand} ${product.productType}`);
        const likelyDuplicate = activeRoutine.find(item => {
            const itemText = routineItemText(item);
            return productText && itemText && (
                normalizeText(product.brand) && itemText.includes(normalizeText(product.brand)) ||
                productText.split(' ').filter(word => word.length > 4 && itemText.includes(word)).length >= 3
            );
        });

        if (sameStep) {
            return {
                status: 'swap',
                title: 'Possible Swap',
                message: `This is another ${String(product.productType || 'routine step').replace(/_/g, ' ')}. Compare it against ${routineItemName(sameStep)} before adding both.`,
                detail: 'Best move: replace the old step only if this scores higher, has lower risk, or solves a concern the old one does not.',
                action: 'Review Routine'
            };
        }

        if (likelyDuplicate) {
            return {
                status: 'duplicate',
                title: 'Looks Similar To Something You Use',
                message: `This may overlap with ${routineItemName(likelyDuplicate)}. Check whether it is truly different before buying.`,
                detail: 'Best move: avoid buying near-duplicates unless you are replacing the existing product.',
                action: 'Review Routine'
            };
        }

        const overlap = concernOverlapForProduct(analysis);

        if (overlap.length > 0) {
            return {
                status: 'overlap',
                title: 'Routine Overlap',
                message: `Your routine already targets ${overlap.slice(0, 2).join(' and ')}. This may be useful, but it may not be the highest-impact next buy.`,
                detail: 'Best move: buy only if your current routine is not improving that concern after several weeks.',
                action: 'Review Routine'
            };
        }

        return {
            status: 'addition',
            title: 'Potential Add-On',
            message: 'This does not duplicate an obvious routine step. Review the score, risk, and ingredient notes before buying.',
            detail: 'Best move: add it only if it fills a missing role or directly targets a priority concern.',
            action: 'Review Routine'
        };
    }

    function goToRoutine() {
        navigate('routine-import');
    }

    function handleNextMoveAction(move) {
        if (!move) return;

        if (move.primaryTarget === 'routine') {
            goToRoutine();
            return;
        }

        if (move.primaryTarget === 'store' && analysis?.product?.url) {
            window.open(analysis.product.url, '_blank', 'noopener');
            return;
        }

        if (move.primaryTarget === 'search') {
            clearSelection();
            return;
        }

        const safetySection = document.getElementById('routine-safety-check');
        if (safetySection) safetySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    function getScoreLabel(score) {
        if (score >= 80) return 'Excellent Match';
        if (score >= 60) return 'Good Match';
        if (score >= 45) return 'Okay Match';
        return 'Not Ideal';
    }

    // The four score colours and three risk colours were off-palette hexes.
    // They are now tones, so Chip and Card render them from the token layer.
    function riskTone(risk) {
        if (risk === 'low') return 'success';
        if (risk === 'moderate') return 'caution';
        return 'danger';
    }

    function ingredientTone(score) {
        return Number(score) >= 4 ? 'danger' : 'caution';
    }

    function sentenceCase(value) {
        const text = String(value || '');
        return text.charAt(0).toUpperCase() + text.slice(1);
    }
</script>

<style>
    /* The screen is a single column that reorders itself: before a scan it is
       masthead + finder; after one, the verdict takes the top and the finder
       drops to the bottom as "check another". Flex `order` does that without
       duplicating the input markup, so the field never remounts mid-flow.

       No media query anywhere in this file. Every former breakpoint (240, 520,
       620, 640, 800) was a fixed width standing in for an intrinsic grid or a
       clamp, and the token layer already provides both. */

    .scan {
        display: flex;
        flex-direction: column;
        gap: var(--space-10);
        max-width: 880px;
        margin: 0 auto;
        padding: var(--page-top) var(--page-pad-x) var(--page-pad-y);
    }

    .masthead { order: 0; text-align: center; }
    .answer-slot { order: 1; }
    .reasoning { order: 2; }
    .finder { order: 3; }

    .masthead h1 {
        margin: 0;
        font-family: var(--font-display);
        font-size: var(--text-3xl);
        font-weight: var(--weight-regular);
        line-height: var(--leading-snug);
        color: var(--cream);
    }

    /* Once there is a verdict, the page title steps down to an eyebrow so the
       answer is the largest type on the screen. */
    .has-result .masthead h1 {
        font-family: var(--font-body);
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-eyebrow);
        text-transform: uppercase;
        color: var(--text-muted);
    }

    .lede {
        max-width: 52ch;
        margin: var(--space-4) auto 0;
        color: var(--text-muted);
        font-size: var(--text-sm);
    }

    /* --- the answer ----------------------------------------------------- */

    .product-line {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--space-4);
        padding-bottom: var(--space-5);
        margin-bottom: var(--space-6);
        border-bottom: 1px solid var(--border);
    }

    .product-thumb {
        width: 56px;
        height: 56px;
        object-fit: cover;
        border: 1px solid var(--border);
        flex: none;
    }

    .product-id { flex: 1 1 14rem; min-width: 0; }

    .product-name { margin: 0; color: var(--cream); font-size: var(--text-sm); }

    .product-meta {
        margin: var(--space-1) 0 0;
        color: var(--text-muted);
        font-size: var(--text-xs);
    }

    .store-link {
        flex: none;
        font-size: var(--text-2xs);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
    }

    .verdict-eyebrow {
        margin: 0 0 var(--space-3);
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-eyebrow);
        text-transform: uppercase;
        color: var(--ink);
    }

    .verdict-headline {
        margin: 0;
        font-family: var(--font-display);
        font-size: var(--text-3xl);
        font-weight: var(--weight-regular);
        line-height: var(--leading-tight);
        color: var(--cream);
    }

    .verdict-body {
        max-width: 56ch;
        margin: var(--space-5) 0 0;
        color: var(--text-muted);
        font-size: var(--text-md);
        line-height: var(--leading-relaxed);
    }

    .verdict-note {
        max-width: 56ch;
        margin: var(--space-4) 0 0;
        color: var(--text-muted);
        font-size: var(--text-sm);
        line-height: var(--leading-normal);
    }

    .readout {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: var(--space-6) var(--space-10);
        margin-top: var(--space-7);
        padding-top: var(--space-6);
        border-top: 1px solid var(--border);
    }

    .readout-side {
        display: flex;
        flex: 1 1 16rem;
        min-width: 0;
        flex-direction: column;
        gap: var(--space-3);
        padding-top: var(--space-1);
    }

    /* The reasons are sentences, not tokens, so they are a hairline-marked
       list. Chip is nowrap by design and would push the page sideways. */
    .reasons {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        margin: 0;
        padding: 0;
        list-style: none;
    }

    .reasons li {
        position: relative;
        padding-left: var(--space-4);
        color: var(--text-muted);
        font-size: var(--text-xs);
        line-height: var(--leading-normal);
    }

    .reasons li::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0.7em;
        width: 8px;
        height: 1px;
        background: var(--border-strong);
    }

    .answer-actions {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-3);
        margin-top: var(--space-7);
    }

    /* --- the reasoning under it ----------------------------------------- */

    .reasoning {
        display: flex;
        flex-direction: column;
        gap: var(--space-6);
    }

    .block-label {
        margin: 0;
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-eyebrow);
        text-transform: uppercase;
        color: var(--text-muted);
    }

    .block-note {
        margin: var(--space-2) 0 var(--space-4);
        color: var(--text-muted);
        font-size: var(--text-xs);
    }

    .alerts {
        display: flex;
        flex-direction: column;
        gap: var(--space-3);
    }

    /* Three read-outs side by side on a wide screen, one column on a phone,
       decided by the content width rather than by a breakpoint. */
    .facets {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
        gap: var(--space-8) var(--space-6);
    }

    .facet h3 {
        margin: 0 0 var(--space-3);
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        color: var(--text-muted);
    }

    .facet-note {
        margin: 0 0 var(--space-3);
        color: var(--text-muted);
        font-size: var(--text-xs);
        line-height: var(--leading-normal);
    }

    .banner-p { margin: 0 0 var(--space-2); }
    .banner-p:last-child { margin-bottom: 0; }
    .banner-action { margin-top: var(--space-3); }

    .reset { display: flex; justify-content: center; }

    /* --- one control group, three doors --------------------------------- */

    .finder {
        display: flex;
        flex-direction: column;
        gap: var(--space-5);
    }

    .profile-line {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: var(--space-2) var(--space-3);
        color: var(--text-muted);
        font-size: var(--text-xs);
    }

    /* Segmented switch. Kept as local markup: Button has no selected state,
       and a mode switch needs one that survives hover. */
    .modes {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        border: 1px solid var(--border);
        margin-bottom: var(--space-6);
    }

    .modes button {
        appearance: none;
        margin: 0;
        padding: var(--space-3) var(--space-4);
        background: transparent;
        border: 0;
        border-bottom: 2px solid transparent;
        border-radius: var(--radius-0);
        color: var(--text-muted);
        font-size: var(--text-2xs);
        font-weight: var(--weight-medium);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        cursor: pointer;
        transition:
            color var(--transition),
            background var(--transition),
            border-color var(--transition);
    }

    .modes button:hover { color: var(--cream); background: var(--hover-veil); }

    .modes button.on {
        color: var(--rose);
        background: var(--surface-2);
        border-bottom-color: var(--rose);
    }

    .search-input {
        padding: var(--space-4) var(--space-5);
        border-radius: var(--radius-0);
        font-size: var(--text-base);
    }

    .search-input:focus { border-color: var(--rose); }

    .drop {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 8rem;
        padding: var(--space-6);
        border: 1px dashed var(--border-strong);
        color: var(--cream);
        text-align: center;
        font-size: var(--text-2xs);
        letter-spacing: var(--track-label);
        text-transform: uppercase;
        cursor: pointer;
        transition: border-color var(--transition), background var(--transition);
    }

    .drop:hover { border-color: var(--rose); background: var(--hover-veil); }

    .drop input { display: none; }

    .shot-preview {
        display: block;
        max-width: 12rem;
        max-height: 15rem;
        object-fit: contain;
        margin: var(--space-5) auto 0;
        border: 1px solid var(--border);
    }

    .detected {
        margin: var(--space-4) 0 0;
        padding: var(--space-3) var(--space-4);
        border: 1px solid var(--border);
        color: var(--text-muted);
        font-size: var(--text-xs);
        line-height: var(--leading-normal);
    }

    .detected strong { color: var(--cream); font-weight: var(--weight-regular); }

    .hits-group { margin-top: var(--space-5); }

    /* A result row is one control, so it stays a <button>: Card must not carry
       the role, and Button is a label, not a row. */
    .hits {
        list-style: none;
        margin: var(--space-3) 0 0;
        padding: 0;
        border: 1px solid var(--border);
    }

    .hits li + li { border-top: 1px solid var(--border); }

    .hits button {
        appearance: none;
        display: flex;
        align-items: center;
        gap: var(--space-4);
        width: 100%;
        margin: 0;
        padding: var(--space-3) var(--space-4);
        background: transparent;
        border: 0;
        border-radius: var(--radius-0);
        color: inherit;
        text-align: left;
        cursor: pointer;
        transition: background var(--transition);
    }

    .hits button:hover { background: var(--surface-2); }

    .hit-image {
        width: 44px;
        height: 44px;
        object-fit: cover;
        border: 1px solid var(--border);
        flex: none;
    }

    .hit-info { display: block; flex: 1; min-width: 0; }
    .hit-name { display: block; color: var(--cream); font-size: var(--text-sm); }
    .hit-brand { display: block; color: var(--text-muted); font-size: var(--text-xs); }
    .hit-price { flex: none; color: var(--rose); font-size: var(--text-sm); }

    .chips {
        display: flex;
        flex-wrap: wrap;
        gap: var(--space-2);
        margin: 0;
        padding: 0;
        list-style: none;
    }

    .chips li { display: flex; }

    .pulse {
        margin: 0;
        padding: var(--space-10) 0;
        text-align: center;
        color: var(--cream);
        font-family: var(--font-display);
        font-size: var(--text-md);
        animation: pulse 2s ease-in-out infinite;
    }

    @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.45; }
    }
</style>

<div class="scan" class:has-result={!!analysis}>
    <header class="masthead">
        <h1>Product Scanner</h1>
        {#if !analysis}
            <p class="lede">Search for a product or upload a screenshot to see if it is right for your skin</p>
        {/if}
    </header>

    {#if isAnalyzing}
        <div class="answer-slot">
            <p class="pulse">{hasSkinProfile ? 'Analyzing product for your skin...' : 'Looking up product details...'}</p>
        </div>
    {/if}

    {#if analysis}
        <!-- The answer. One card, one tone, the decision in display type. -->
        <section class="answer-slot" aria-label="Verdict">
            <Card tone={verdictTone} elevated style={`--ink: ${verdictInk}`}>
                <div class="product-line">
                    {#if analysis.product.imageUrl}
                        <img class="product-thumb" src={analysis.product.imageUrl} alt="" />
                    {/if}
                    <div class="product-id">
                        <p class="product-name">{analysis.product.name}</p>
                        <p class="product-meta">{analysis.product.brand} · {analysis.product.productType} · ${analysis.product.price} · {analysis.product.rating.toFixed(1)} ({analysis.product.reviews} reviews)</p>
                    </div>
                    <a class="store-link" href={analysis.product.url} target="_blank" rel="noopener">View on store</a>
                </div>

                <p class="verdict-eyebrow">{verdictEyebrow}</p>
                <h2 class="verdict-headline">{verdictHeadline}</h2>
                <p class="verdict-body">{verdictBody}</p>

                {#if isPersonalizedAnalysis}
                    {#if analysis.verdict}
                        <p class="verdict-note">{analysis.verdict}</p>
                    {/if}

                    <div class="readout">
                        <Stat
                            value={`${analysis.compatibilityScore}%`}
                            label="Skin match"
                            caption={getScoreLabel(analysis.compatibilityScore)}
                            align="start"
                        />
                        <div class="readout-side">
                            <ul class="chips">
                                <li><Chip tone={riskTone(analysis.breakoutRisk)} value={sentenceCase(analysis.breakoutRisk)}>Breakout risk</Chip></li>
                            </ul>
                            {#if verdictChips.length > 0}
                                <ul class="reasons">
                                    {#each verdictChips as bullet}
                                        <li>{bullet}</li>
                                    {/each}
                                </ul>
                            {/if}
                        </div>
                    </div>
                {/if}

                <div class="answer-actions">
                    {#if isPersonalizedAnalysis && nextMove}
                        <Button on:click={() => handleNextMoveAction(nextMove)}>{nextMove.primaryAction}</Button>
                        {#if nextMove.primaryTarget !== 'routine' && activeRoutine.length > 0}
                            <Button variant="secondary" on:click={goToRoutine}>Review routine</Button>
                        {/if}
                    {:else}
                        <Button on:click={goToSkinAnalysis}>Analyze my skin</Button>
                    {/if}
                </div>
            </Card>
        </section>

        <!-- Everything below the fold of the answer is the reasoning for it. -->
        <div class="reasoning">
            {#if isPersonalizedAnalysis && routineComparison}
                <Banner tone={COMPARE_TONE[routineComparison.status] || 'info'} title={routineComparison.title}>
                    <p class="banner-p">{routineComparison.message}</p>
                    {#if routineComparison.detail}
                        <p class="banner-p">{routineComparison.detail}</p>
                    {/if}
                    <div class="banner-action">
                        <Button variant="ghost" size="sm" on:click={goToRoutine}>{routineComparison.action}</Button>
                    </div>
                </Banner>
            {/if}

            {#if isPersonalizedAnalysis && routineAlerts.length > 0}
                <section id="routine-safety-check">
                    <h3 class="block-label">Routine safety check</h3>
                    <p class="block-note">Active overlap, duplicate steps, and timing</p>
                    <div class="alerts">
                        {#each routineAlerts as alert}
                            <Banner tone={ALERT_TONE[alert.tone] || 'info'} title={alert.title}>{alert.body}</Banner>
                        {/each}
                    </div>
                </section>
            {/if}

            <Card>
                <div class="facets">
                    {#if analysis.benefits.length > 0}
                        <div class="facet">
                            <h3>{isPersonalizedAnalysis ? 'What it does for you' : 'Likely benefits'}</h3>
                            <ul class="chips">
                                {#each analysis.benefits as benefit}
                                    <li><Chip tone="success">{benefit.concern}</Chip></li>
                                {/each}
                            </ul>
                        </div>
                    {:else if !isPersonalizedAnalysis && analysis.productRoleBenefits && analysis.productRoleBenefits.length > 0}
                        <div class="facet">
                            <h3>Likely benefits</h3>
                            <ul class="chips">
                                {#each analysis.productRoleBenefits as benefit}
                                    <li><Chip tone="success">{benefit}</Chip></li>
                                {/each}
                            </ul>
                        </div>
                    {/if}

                    {#if isPersonalizedAnalysis && analysis.notAddressed.length > 0}
                        <div class="facet">
                            <h3>Not designed for</h3>
                            <p class="facet-note">These are your concerns this product type doesn't target, which is normal.</p>
                            <ul class="chips">
                                {#each analysis.notAddressed as concern}
                                    <li><Chip>{concern}</Chip></li>
                                {/each}
                            </ul>
                        </div>
                    {/if}

                    <div class="facet">
                        <h3>Comedogenic ingredients</h3>
                        <ul class="chips">
                            {#if analysis.comedogenicIngredients.length > 0}
                                {#each analysis.comedogenicIngredients as ing}
                                    <li><Chip tone={ingredientTone(ing.score)} value={`${ing.score}/5`}>{ing.ingredient}</Chip></li>
                                {/each}
                            {:else}
                                <li><Chip tone="success">None detected</Chip></li>
                            {/if}
                        </ul>
                    </div>
                </div>
            </Card>

            {#if isPersonalizedAnalysis && !isLoggedIn}
                <Banner tone="info" title="This match is not saved yet">
                    <p class="banner-p">It is personalized from your temporary skin profile. Sign in to save product scans and keep your history across devices.</p>
                    <div class="banner-action">
                        <Button variant="secondary" size="sm" on:click={signInToSave}>Sign in to save</Button>
                    </div>
                </Banner>
            {/if}

            <div class="reset">
                <Button variant="ghost" on:click={clearSelection}>Scan another product</Button>
            </div>
        </div>
    {/if}

    <!-- Search and screenshot are two doors to the same room, so they are one
         panel with one switch rather than two panels competing for the eye. -->
    <section class="finder">
        {#if !hasSkinProfile}
            <Banner tone="info" title="SkinGenie needs your skin profile first">
                <p class="banner-p">Search or upload a product to see what it is, what it may help with, and any ingredient risk notes. To answer “is this right for me?”, add your skin profile.</p>
                <div class="banner-action">
                    <Button variant="secondary" size="sm" on:click={goToSkinAnalysis}>Analyze my skin</Button>
                </div>
            </Banner>
        {:else}
            <div class="profile-line">
                <span>{profileSource === 'saved' ? 'Using your saved skin profile' : "Using this browser's temporary skin profile"}</span>
                <ul class="chips">
                    {#each userConcerns as concern}
                        <li><Chip>{concern}</Chip></li>
                    {/each}
                </ul>
                {#if !isLoggedIn}
                    <Button variant="ghost" size="sm" on:click={signInToSave}>Sign in to save scans</Button>
                {/if}
            </div>
        {/if}

        <Card>
            <div class="modes" role="group" aria-label="How to find the product">
                <button
                    class:on={scanMode === 'search'}
                    aria-pressed={scanMode === 'search'}
                    type="button"
                    on:click={() => scanMode = 'search'}
                >Search</button>
                <button
                    class:on={scanMode === 'screenshot'}
                    aria-pressed={scanMode === 'screenshot'}
                    type="button"
                    on:click={() => scanMode = 'screenshot'}
                >Screenshot</button>
            </div>

            {#if scanMode === 'search'}
                <Field label="Product name or brand" hint="Start typing, then pick the match." let:id>
                    <input
                        {id}
                        class="search-input"
                        type="text"
                        placeholder="Search for a product (e.g. CeraVe, Retinol Serum...)"
                        bind:value={searchQuery}
                        on:input={debounceSearch}
                    />
                </Field>

                {#if searchResults.length > 0}
                    <ul class="hits">
                        {#each searchResults as result}
                            <li>
                                <button type="button" on:click={() => analyzeProduct(result)}>
                                    {#if result.imageUrl}
                                        <img class="hit-image" src={result.imageUrl} alt="" />
                                    {/if}
                                    <span class="hit-info">
                                        <span class="hit-name">{result.name}</span>
                                        <span class="hit-brand">{result.brand} · {result.productType}</span>
                                    </span>
                                    <span class="hit-price">${result.price}</span>
                                </button>
                            </li>
                        {/each}
                    </ul>
                {/if}
            {:else}
                <Field
                    label="Product screenshot"
                    hint="Use a screenshot from social media, Amazon, Sephora, Ulta, or a brand page. SkinGenie will suggest catalog matches for you to confirm."
                    let:id
                >
                    <label class="drop">
                        <input {id} type="file" accept="image/png,image/jpeg,image/webp" on:change={handleScreenshotUpload} />
                        Upload product screenshot
                    </label>
                </Field>

                {#if screenshotPreview}
                    <img class="shot-preview" src={screenshotPreview} alt="Uploaded product screenshot preview" />
                {/if}

                {#if isDetecting}
                    <p class="pulse">Reading screenshot...</p>
                {/if}

                {#if detectedText}
                    <p class="detected"><strong>Detected text:</strong> {detectedText}</p>
                {/if}

                {#if detectionError}
                    <div class="hits-group">
                        <Banner tone="caution">{detectionError}</Banner>
                    </div>
                {/if}

                {#if screenshotMatches.length > 0}
                    <div class="hits-group">
                        <h3 class="block-label">Confirm the product</h3>
                        <ul class="hits">
                            {#each screenshotMatches as result}
                                <li>
                                    <button type="button" on:click={() => analyzeProduct(result)}>
                                        {#if result.imageUrl}
                                            <img class="hit-image" src={result.imageUrl} alt="" />
                                        {/if}
                                        <span class="hit-info">
                                            <span class="hit-name">{result.name}</span>
                                            <span class="hit-brand">{result.brand} · {result.productType}</span>
                                        </span>
                                        <span class="hit-price">${result.price}</span>
                                    </button>
                                </li>
                            {/each}
                        </ul>
                    </div>
                {/if}
            {/if}
        </Card>
    </section>
</div>
